import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { logoutAdmin, checkAdminAuth, handleAdminAuthError } from '../utils/auth';
import './AdminBankAccounts.css';
import { API_URL } from '../config/data';

const AdminBankAccounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCurrency, setFilterCurrency] = useState('all');

  useEffect(() => {
    const initAdmin = async () => {
      try {
        await checkAdminAuth();
        await fetchAccounts();
      } catch (error) {
        handleAdminAuthError(error);
      }
    };

    initAdmin();
  }, [navigate]);

  const fetchAccounts = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('AdminBankAccounts - Token:', token ? 'Present' : 'Missing');
      console.log('AdminBankAccounts - Token value:', token);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (filterStatus !== 'all') {
        params.append('isActive', filterStatus === 'active' ? 'true' : 'false');
      }
      if (filterCurrency !== 'all') {
        params.append('currency', filterCurrency);
      }

      const response = await axios.get(`${API_URL}/api/admin/bank-accounts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAccounts(response.data.accounts || []);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      setError('Ошибка загрузки банковских счетов');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAccounts(1);
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот банковский счет? Это действие нельзя отменить и удалит все связанные операции.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      console.log('AdminBankAccounts - Token:', token ? 'Present' : 'Missing');
      console.log('AdminBankAccounts - Token value:', token);
      
      await axios.delete(`${API_URL}/api/admin/bank-accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchAccounts(currentPage);
    } catch (error) {
      console.error('Error deleting bank account:', error);
      setError('Ошибка удаления банковского счета: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency || 'RUB'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogout = () => {
    logoutAdmin();
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading__spinner"></div>
        <p>Загрузка банковских счетов...</p>
      </div>
    );
  }

  return (
    <div className="admin-bank-accounts">
      <div className="admin-header">
        <div className="admin-header__logo">
          <img src="/assets/img/logo.svg" alt="Pro Investor" />
        </div>
        <div className="admin-header__title">Банковские счета</div>
        <div className="admin-header__actions">
          <button 
            className="admin-header__back" 
            onClick={() => navigate('/admin')}
          >
            <span className="--icon-arrow-left"></span>
            Назад
          </button>
          <button className="admin-header__logout" onClick={handleLogout}>
            <span className="--icon-log-out"></span>
            Выйти
          </button>
        </div>
      </div>

      <div className="admin-content">
        {error && (
          <div className="admin-error">
            <p>{error}</p>
            <button className="admin-btn admin-btn--primary" onClick={() => setError('')}>
              Закрыть
            </button>
          </div>
        )}

        <div className="admin-bank-accounts__filters">
          <form onSubmit={handleSearch} className="admin-search-form">
            <input
              type="text"
              placeholder="Поиск по номеру счета или владельцу..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-search-input"
            />
            <button type="submit" className="admin-search-btn">
              <span className="--icon-search"></span>
            </button>
          </form>

          <div className="admin-filters">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="admin-filter-select"
            >
              <option value="all">Все статусы</option>
              <option value="active">Активные</option>
              <option value="inactive">Неактивные</option>
            </select>

            <select
              value={filterCurrency}
              onChange={(e) => setFilterCurrency(e.target.value)}
              className="admin-filter-select"
            >
              <option value="all">Все валюты</option>
              <option value="RUB">RUB</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </div>

        <div className="admin-bank-accounts__table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Номер счета</th>
                <th>Владелец</th>
                <th>Банк</th>
                <th>Баланс</th>
                <th>Валюта</th>
                <th>Статус</th>
                <th>Дата создания</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td className="admin-table__id">{account.id}</td>
                  <td className="admin-table__account-number">
                    {account.number}
                  </td>
                  <td className="admin-table__owner">
                    <div className="admin-table__owner-info">
                      <div className="admin-table__owner-name">
                        {account.first_name} {account.last_name}
                      </div>
                      <div className="admin-table__owner-email">
                        {account.email}
                      </div>
                    </div>
                  </td>
                  <td className="admin-table__bank">
                    {account.bank}
                  </td>
                  <td className="admin-table__balance">
                    {formatCurrency(account.balance, account.currency)}
                  </td>
                  <td className="admin-table__currency">
                    {account.currency}
                  </td>
                  <td className="admin-table__status">
                    <span className={`admin-status admin-status--${account.is_active ? 'active' : 'inactive'}`}>
                      {account.is_active ? 'Активный' : 'Неактивный'}
                    </span>
                  </td>
                  <td className="admin-table__date">
                    {formatDate(account.created_at)}
                  </td>
                  <td className="admin-table__actions">
                    <button
                      className="admin-btn admin-btn--danger"
                      onClick={() => handleDeleteAccount(account.id)}
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {accounts.length === 0 && (
            <div className="admin-empty">
              <h2>Банковские счета не найдены</h2>
              <p>Попробуйте изменить параметры поиска или фильтры</p>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="admin-pagination">
            <button
              className="admin-pagination__btn"
              onClick={() => fetchAccounts(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Назад
            </button>
            
            <div className="admin-pagination__info">
              Страница {currentPage} из {totalPages}
            </div>
            
            <button
              className="admin-pagination__btn"
              onClick={() => fetchAccounts(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Вперед
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBankAccounts;
